export interface BusinessHealthKPI {
  id: string;
  label: string;
  value: string;
  icon: string;
  trend: number;
  trendLabel: string;
  accent: 'primary' | 'accent' | 'secondary' | 'success' | 'warning' | 'danger';
}

export interface AttentionItem {
  id: number;
  title: string;
  description: string;
  radioName: string;
  badge: string;
  badgeAccent: 'warning' | 'danger';
  route: string;
}

export interface GrowthMetric {
  id: string;
  label: string;
  value: string;
  icon: string;
  trend: number;
  trendLabel: string;
  accent: 'primary' | 'accent' | 'secondary' | 'success';
}

export interface TimelineEvent {
  id: number;
  type: 'nueva_radio' | 'nuevo_pago' | 'contrato_firmado' | 'nuevo_programa' | 'nuevo_evento';
  title: string;
  description: string;
  radioName: string;
  timeRelative: string;
  amount?: string;
}

export interface SADashboardData {
  businessHealth: BusinessHealthKPI[];
  attentionItems: AttentionItem[];
  growthMetrics: GrowthMetric[];
  timeline: TimelineEvent[];
}

export const saDashboardData: SADashboardData = {
  businessHealth: [
    {
      id: 'radios-activas',
      label: 'Radios activas',
      value: '803',
      icon: 'ri-radio-line',
      trend: 12.4,
      trendLabel: 'vs mes anterior',
      accent: 'primary',
    },
    {
      id: 'oyentes-activos',
      label: 'Oyentes activos',
      value: '245,280',
      icon: 'ri-user-voice-line',
      trend: 18.7,
      trendLabel: 'vs mes anterior',
      accent: 'accent',
    },
    {
      id: 'ingresos-mes',
      label: 'Ingresos del mes',
      value: '$21,400',
      icon: 'ri-money-dollar-circle-line',
      trend: 19.3,
      trendLabel: 'vs mes anterior',
      accent: 'success',
    },
    {
      id: 'solicitudes-pendientes',
      label: 'Solicitudes pendientes',
      value: '12',
      icon: 'ri-file-list-3-line',
      trend: 4.5,
      trendLabel: 'vs semana anterior',
      accent: 'warning',
    },
  ],
  attentionItems: [
    {
      id: 1,
      title: 'Radios pendientes de aprobación',
      description: 'Esperando revisión desde hace 3 días',
      radioName: 'Radio Ritmo Latino',
      badge: '8 nuevas',
      badgeAccent: 'warning',
      route: '/sa/radio-approvals',
    },
    {
      id: 2,
      title: 'Contratos pendientes de firma',
      description: 'Enviado — sin respuesta del cliente',
      radioName: 'Radio Éxitos TV',
      badge: '5 días',
      badgeAccent: 'warning',
      route: '/sa/contracts',
    },
    {
      id: 3,
      title: 'Facturas vencidas',
      description: 'Plan Pro — Pago pendiente desde el 10 de junio',
      radioName: 'Radio Sonora Digital',
      badge: '7 días',
      badgeAccent: 'danger',
      route: '/sa/invoices',
    },
    {
      id: 4,
      title: 'Tickets urgentes sin asignar',
      description: 'Problema de facturación — Cliente no puede pagar',
      radioName: 'Radio Urbana 101',
      badge: '4 horas',
      badgeAccent: 'danger',
      route: '/sa/support',
    },
  ],
  growthMetrics: [
    {
      id: 'radios-nuevas',
      label: 'Radios nuevas',
      value: '+24',
      icon: 'ri-radio-button-line',
      trend: 14.2,
      trendLabel: 'este mes',
      accent: 'primary',
    },
    {
      id: 'nuevos-oyentes',
      label: 'Nuevos oyentes',
      value: '+18,430',
      icon: 'ri-user-add-line',
      trend: 22.5,
      trendLabel: 'este mes',
      accent: 'accent',
    },
    {
      id: 'nuevos-seguidores',
      label: 'Nuevos seguidores',
      value: '+9,720',
      icon: 'ri-heart-add-line',
      trend: 10.8,
      trendLabel: 'este mes',
      accent: 'secondary',
    },
    {
      id: 'nuevos-programas',
      label: 'Nuevos programas',
      value: '+156',
      icon: 'ri-mic-line',
      trend: 31.4,
      trendLabel: 'este mes',
      accent: 'success',
    },
  ],
  timeline: [
    {
      id: 1,
      type: 'nueva_radio',
      title: 'Nueva radio',
      description: 'se unió a la plataforma con plan Free',
      radioName: 'Radio Ritmo Latino',
      timeRelative: 'Hace 8 min',
    },
    {
      id: 2,
      type: 'nuevo_pago',
      title: 'Nuevo pago',
      description: 'Pagó su plan mensual Pro',
      radioName: 'Radio Nueva Actitud',
      amount: '$29.00',
      timeRelative: 'Hace 45 min',
    },
    {
      id: 3,
      type: 'contrato_firmado',
      title: 'Contrato firmado',
      description: 'Firmó contrato Premium — Radio activada',
      radioName: 'Radio Stereo Visión',
      timeRelative: 'Hace 2 horas',
    },
    {
      id: 4,
      type: 'nuevo_programa',
      title: 'Nuevo programa',
      description: 'creó "Mañanas con Energía" — L-V 8:00 AM',
      radioName: 'Radio Onda Digital',
      timeRelative: 'Hace 3 horas',
    },
    {
      id: 5,
      type: 'nuevo_pago',
      title: 'Nuevo pago',
      description: 'Pagó licencia anual de frecuencia',
      radioName: 'Radio Clásica FM',
      amount: '$49.95',
      timeRelative: 'Hace 5 horas',
    },
    {
      id: 6,
      type: 'nuevo_evento',
      title: 'Nuevo evento',
      description: 'agendó "Festival de Verano 2026" para el 20 de julio',
      radioName: 'Radio Fiesta Mix',
      timeRelative: 'Ayer',
    },
    {
      id: 7,
      type: 'nueva_radio',
      title: 'Nueva radio',
      description: 'se unió a la plataforma con plan Free',
      radioName: 'La Voz del Llano',
      timeRelative: 'Ayer',
    },
    {
      id: 8,
      type: 'nuevo_programa',
      title: 'Nuevo programa',
      description: 'creó "Noches de Jazz" — S-D 21:00',
      radioName: 'Radio Matinal FM',
      timeRelative: 'Ayer',
    },
    {
      id: 9,
      type: 'contrato_firmado',
      title: 'Contrato firmado',
      description: 'Firmó contrato Business — 10 usuarios incluidos',
      radioName: 'Radio Urbana 101',
      timeRelative: 'Hace 2 días',
    },
    {
      id: 10,
      type: 'nuevo_evento',
      title: 'Nuevo evento',
      description: 'agendó "Torneo de Locutores" para el 5 de agosto',
      radioName: 'Radio Éxitos TV',
      timeRelative: 'Hace 2 días',
    },
  ],
};

/* ═══════════════════════════════════════════════════════════════
   NOC — CENTRO DE OPERACIONES (SA-001)
   ═══════════════════════════════════════════════════════════════ */

export interface NocKPI {
  id: string;
  label: string;
  value: string;
  icon: string;
  trend: number;
  trendLabel: string;
  status: 'green' | 'yellow' | 'red';
}

export interface PlatformService {
  id: string;
  name: string;
  icon: string;
  status: 'online' | 'degraded' | 'offline';
  lastCheck: string;
  responseTime: string;
}

export interface NocCriticalAlert {
  id: number;
  type: 'service_down' | 'backup_failed' | 'license_expiring' | 'storage_low' | 'api_disconnected';
  title: string;
  description: string;
  severity: 'critical' | 'warning';
  timeRelative: string;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  route: string;
  accent: 'primary' | 'accent' | 'secondary' | 'warning' | 'danger';
}

export interface NocActivity {
  id: number;
  type: 'radio_registrada' | 'pago' | 'aprobacion' | 'ticket' | 'error' | 'broadcast';
  title: string;
  description: string;
  entity: string;
  timeRelative: string;
  metadata?: string;
}

export const nocKpis: NocKPI[] = [
  { id: 'radios-registradas', label: 'Radios Registradas', value: '11,342', icon: 'ri-radio-line', trend: 8.3, trendLabel: 'vs mes anterior', status: 'green' },
  { id: 'radios-en-vivo', label: 'Radios En Vivo', value: '847', icon: 'ri-broadcast-line', trend: 12.1, trendLabel: 'vs hora anterior', status: 'green' },
  { id: 'radios-offline', label: 'Radios Offline', value: '124', icon: 'ri-stop-circle-line', trend: -3.2, trendLabel: 'vs semana anterior', status: 'yellow' },
  { id: 'usuarios-totales', label: 'Usuarios Totales', value: '1.2M', icon: 'ri-user-line', trend: 15.7, trendLabel: 'vs mes anterior', status: 'green' },
  { id: 'oyentes-activos', label: 'Oyentes Activos', value: '245,280', icon: 'ri-user-voice-line', trend: 18.7, trendLabel: 'vs mes anterior', status: 'green' },
  { id: 'locutores-activos', label: 'Locutores Activos', value: '4,892', icon: 'ri-mic-line', trend: 6.4, trendLabel: 'vs semana anterior', status: 'green' },
  { id: 'programas-en-vivo', label: 'Programas En Vivo', value: '328', icon: 'ri-live-line', trend: 22.3, trendLabel: 'vs hora anterior', status: 'green' },
  { id: 'espacio-utilizado', label: 'Espacio Utilizado', value: '4.2 TB', icon: 'ri-hard-drive-2-line', trend: 9.8, trendLabel: 'vs mes anterior', status: 'yellow' },
  { id: 'almacenamiento-disponible', label: 'Almacenamiento Disponible', value: '48.5 TB', icon: 'ri-database-2-line', trend: -5.1, trendLabel: 'vs mes anterior', status: 'yellow' },
  { id: 'consumo-mensual', label: 'Consumo Mensual', value: '$18,420', icon: 'ri-money-dollar-circle-line', trend: 4.2, trendLabel: 'vs mes anterior', status: 'green' },
];

export const platformServices: PlatformService[] = [
  { id: 'supabase', name: 'Supabase', icon: 'ri-database-2-line', status: 'online', lastCheck: 'Hace 12s', responseTime: '87ms' },
  { id: 'api', name: 'API RFW', icon: 'ri-cloud-line', status: 'online', lastCheck: 'Hace 8s', responseTime: '42ms' },
  { id: 'streaming', name: 'Streaming', icon: 'ri-broadcast-line', status: 'online', lastCheck: 'Hace 15s', responseTime: '124ms' },
  { id: 'smtp', name: 'SMTP', icon: 'ri-mail-send-line', status: 'online', lastCheck: 'Hace 45s', responseTime: '210ms' },
  { id: 'storage', name: 'Storage', icon: 'ri-hard-drive-3-line', status: 'online', lastCheck: 'Hace 20s', responseTime: '156ms' },
  { id: 'ia', name: 'IA', icon: 'ri-robot-2-line', status: 'degraded', lastCheck: 'Hace 30s', responseTime: '1.4s' },
  { id: 'cdn', name: 'CDN', icon: 'ri-global-line', status: 'online', lastCheck: 'Hace 10s', responseTime: '63ms' },
  { id: 'notificaciones', name: 'Notificaciones', icon: 'ri-notification-3-line', status: 'online', lastCheck: 'Hace 25s', responseTime: '98ms' },
  { id: 'backups', name: 'Backups', icon: 'ri-file-copy-2-line', status: 'online', lastCheck: 'Hace 2min', responseTime: '3.2s' },
];

export const nocCriticalAlerts: NocCriticalAlert[] = [
  { id: 1, type: 'service_down', title: 'Servicio IA Degradado', description: 'El servicio de IA está respondiendo con latencia elevada. Modelo de recomendaciones afectado.', severity: 'warning', timeRelative: 'Hace 12 min' },
  { id: 2, type: 'backup_failed', title: 'Backup Fallido — Reseller 2', description: 'El backup programado de las 04:00 UTC falló. Último backup exitoso: ayer 04:00.', severity: 'critical', timeRelative: 'Hace 4 horas' },
  { id: 3, type: 'license_expiring', title: 'Licencia Anual por Vencer', description: 'Radio Éxitos TV — Licencia anual vence en 7 días. Plan Pro. Sin renovación programada.', severity: 'warning', timeRelative: 'Vence en 7 días' },
  { id: 4, type: 'storage_low', title: 'Espacio Bajo — Reseller 3', description: 'Reseller 3 alcanzó el 87% de capacidad. Quedan 1.2 TB disponibles de 9 TB.', severity: 'warning', timeRelative: '85% ocupado' },
  { id: 5, type: 'api_disconnected', title: 'API WhatsApp Desconectada', description: 'La integración de WhatsApp Business API perdió conexión. Mensajes automáticos detenidos.', severity: 'critical', timeRelative: 'Hace 35 min' },
  { id: 6, type: 'license_expiring', title: '3 Licencias Próximas a Vencer', description: 'Radio Stereo Visión, Radio Onda Digital y Radio Clásica FM vencen en menos de 15 días.', severity: 'warning', timeRelative: 'Próximos 15 días' },
];

export const quickActions: QuickAction[] = [
  { id: 'nueva-radio', label: 'Nueva Radio', icon: 'ri-radio-button-line', route: '/sa/radios', accent: 'primary' },
  { id: 'nueva-frecuencia', label: 'Nueva Frecuencia', icon: 'ri-radar-line', route: '/sa/frequencies', accent: 'accent' },
  { id: 'nuevo-plan', label: 'Nuevo Plan', icon: 'ri-stack-line', route: '/sa/plans', accent: 'secondary' },
  { id: 'broadcast-global', label: 'Broadcast Global', icon: 'ri-global-line', route: '/sa/broadcast-center', accent: 'warning' },
  { id: 'feature-flags', label: 'Feature Flags', icon: 'ri-toggle-line', route: '/sa/feature-flags', accent: 'primary' },
  { id: 'usuarios', label: 'Usuarios', icon: 'ri-group-line', route: '/sa/broadcasters', accent: 'secondary' },
  { id: 'configuracion', label: 'Configuración', icon: 'ri-settings-3-line', route: '/sa/settings', accent: 'danger' },
];

export const nocActivities: NocActivity[] = [
  { id: 1, type: 'radio_registrada', title: 'Nueva radio registrada', description: 'se unió a la plataforma con plan Free', entity: 'Radio Ritmo Latino', timeRelative: 'Hace 8 min', metadata: 'Plan Free' },
  { id: 2, type: 'pago', title: 'Nuevo pago recibido', description: 'Pagó su plan mensual Pro', entity: 'Radio Nueva Actitud', timeRelative: 'Hace 45 min', metadata: '$29.00' },
  { id: 3, type: 'aprobacion', title: 'Aprobación completada', description: 'Fue aprobada por el revisor Admin Global', entity: 'Radio Stereo Visión', timeRelative: 'Hace 2 horas', metadata: 'Plan Premium' },
  { id: 4, type: 'ticket', title: 'Ticket urgente abierto', description: 'Problema de facturación — Cliente no puede pagar', entity: 'Radio Urbana 101', timeRelative: 'Hace 4 horas', metadata: '#4829' },
  { id: 5, type: 'error', title: 'Error de streaming detectado', description: 'Stream caído por 3 minutos. Reconexión automática exitosa.', entity: 'Radio Clásica FM', timeRelative: 'Hace 6 horas', metadata: '3min downtime' },
  { id: 6, type: 'broadcast', title: 'Broadcast global enviado', description: 'Mensaje de mantenimiento programado enviado a 11,342 radios', entity: 'Admin Global', timeRelative: 'Hace 8 horas', metadata: '11.3K destinatarios' },
  { id: 7, type: 'radio_registrada', title: 'Nueva radio registrada', description: 'se unió a la plataforma con plan Free', entity: 'La Voz del Llano', timeRelative: 'Ayer', metadata: 'Plan Free' },
  { id: 8, type: 'pago', title: 'Nuevo pago recibido', description: 'Pagó licencia anual de frecuencia', entity: 'Radio Éxitos TV', timeRelative: 'Ayer', metadata: '$49.95' },
  { id: 9, type: 'aprobacion', title: 'Aprobación completada', description: 'Fue aprobada — Frecuencia RFW asignada automáticamente', entity: 'Radio Matinal FM', timeRelative: 'Ayer', metadata: '124.83 RFW' },
  { id: 10, type: 'ticket', title: 'Ticket resuelto', description: 'Resuelto — Configuración de dominio personalizada completada', entity: 'Radio Sonora Digital', timeRelative: 'Ayer', metadata: '#4812' },
  { id: 11, type: 'error', title: 'Error de base de datos', description: 'Timeout en consulta de analytics. Optimizado y resuelto.', entity: 'Supabase — Analytics', timeRelative: 'Ayer', metadata: '12s timeout' },
  { id: 12, type: 'broadcast', title: 'Broadcast global enviado', description: 'Anuncio de nueva funcionalidad: Top 10 por Categoría', entity: 'Admin Global', timeRelative: 'Ayer', metadata: '11.3K destinatarios' },
  { id: 13, type: 'radio_registrada', title: 'Nueva radio registrada', description: 'se unió con plan Business — 10 usuarios incluidos', entity: 'Radio Corporativa Global', timeRelative: 'Hace 2 días', metadata: 'Plan Business' },
  { id: 14, type: 'pago', title: 'Nuevo pago recibido', description: 'Primer pago — Plan Pro mensual', entity: 'Radio Fiesta Mix', timeRelative: 'Hace 2 días', metadata: '$29.00' },
  { id: 15, type: 'aprobacion', title: 'Aprobación completada', description: 'Verificación de documentos completada exitosamente', entity: 'Radio Comunitaria Unida', timeRelative: 'Hace 2 días', metadata: 'Plan Free' },
  { id: 16, type: 'ticket', title: 'Ticket escalado a SA', description: 'Radio reporta problemas con asignación de frecuencia premium', entity: 'Radio Digital Plus', timeRelative: 'Hace 2 días', metadata: '#4798' },
  { id: 17, type: 'error', title: 'CDN — Latencia elevada', description: 'Latencia de CDN superó 500ms en región LATAM. Auto-escalado activado.', entity: 'CDN — Región LATAM', timeRelative: 'Hace 3 días', metadata: '512ms peak' },
  { id: 18, type: 'broadcast', title: 'Broadcast global enviado', description: 'Recordatorio de actualización de términos y condiciones', entity: 'Admin Global', timeRelative: 'Hace 3 días', metadata: '11.3K destinatarios' },
];